import { Subscriber, SubscribersSortBy, SubscribersSortOrder } from 'types/electionService';
import { action, computed, makeObservable, observable } from 'mobx';
import { FiltersObject, PaginationDelegate } from 'store/general/paginationDelegate';
import EmployeeDelegate from './employeeDelegate';
import { EmployeeActionObject } from './actionRules';

export interface DefaultDomainParams {
  groupId: string;
  periodId: string;
  /** Present on RFQ / quote-checklist flows; passed into action handlers for navigation. */
  cartId?: string;
}

// TESTING_POLICY: CHILD_CLASS_ONLY

export default abstract class IAddEmployeesPage<Filters extends FiltersObject> {
  protected _domainParams: DefaultDomainParams;
  protected _defaultFilters: Filters;
  protected _employees: EmployeeDelegate[] = [];
  protected _isLoading: boolean = false;
  protected _availableActions: EmployeeActionObject[];

  protected _createEmployeeDelegates(employees: Subscriber[]): void {
    this._employees = employees.map(
      (employee) =>
        new EmployeeDelegate(
          employee,
          this._availableActions,
          this._domainParams,
          this.getEmployees.bind(this),
          this._setIsLoading.bind(this),
        ),
    );
  }

  protected _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public paginationDelegate;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get employees(): EmployeeDelegate[] {
    return this._employees;
  }

  abstract get stepCanComplete(): boolean;

  public abstract getEmployees(): Promise<void>;

  constructor(filters: Filters, availableActions: EmployeeActionObject[], domainParams: DefaultDomainParams) {
    this._domainParams = domainParams;
    this._defaultFilters = filters;
    this._availableActions = availableActions;

    this.paginationDelegate = new PaginationDelegate<SubscribersSortBy, SubscribersSortOrder, Filters>({
      defaultSortBy: SubscribersSortBy.NAME,
      defaultSortOrder: SubscribersSortOrder.DESC,
      filters: this._defaultFilters,
    });

    makeObservable<
      IAddEmployeesPage<Filters>,
      | '_employees'
      | '_isLoading'
      | '_setIsLoading'
      | '_defaultFilters'
      | '_createEmployeeDelegates'
      | '_availableActions'
    >(this, {
      _employees: observable,
      _isLoading: observable,
      _availableActions: observable,
      paginationDelegate: observable,
      _defaultFilters: observable.shallow,
      isLoading: computed,
      employees: computed,
      _setIsLoading: action.bound,
      _createEmployeeDelegates: action.bound,
    });
  }
}
